import React from 'react';
import { motion } from 'framer-motion';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import { HmiCrop } from './HmiView';
import { HMI_STATE, TARGET, pad } from './screenData';
import { EASE, Intro, Reveal } from './ui';
import styles from './ScreenInspector.module.css';

const T = TARGET['overview.pressure_value'];
const STATES = ['ready', 'running', 'fault'];
const QUESTION = { zh: '图片中排气压力的读数是多少？', en: 'What is the discharge pressure reading?' };

// The page method export_reading writes, in the same shape as the real export
// (mcp_test/pages/*.py): the docstring carries the question, the target and the trial evidence.
function methodCode(lang) {
    const q = QUESTION[lang];
    const doc = lang === 'zh'
        ? ['总览页的排气压力（bar）', '', `read_number “${q}” @ overview.pressure_value`, '试通：3 张全对，覆盖 3 种答案（0.00、1.02、38.00）']
        : ['Discharge pressure on the Overview page (bar)', '', `read_number "${q}" @ overview.pressure_value`, 'Passed: 3 / 3 correct, 3 different answers (0.00, 1.02, 38.00)'];
    const shots = STATES.map((s) => `    overview_${s}.png → ${HMI_STATE[s].pressure}`);
    return [
        [['k', 'class'], [' '], ['f', 'OverviewPage'], ['(Page):']],
        [],
        [['    '], ['k', 'def'], [' '], ['f', 'discharge_pressure'], ['(self) -> float:']],
        [['d', `        """${doc[0]}`]],
        ...doc.slice(1).map((line) => [['d', line ? `        ${line}` : '']]),
        ...shots.map((line) => [['d', `        ${line}`]]),
        [['d', '        """']],
        [['        self.hmi.wait_for('], ['s', '"overview.pressure_value"'], [', timeout=10)']],
        [['        r = self.inspector.'], ['f', 'read_number'], ['(']],
        [['            self.hmi.shot(), '], ['s', `"${q}"`], [',']],
        [['            region=self.ui['], ['s', '"overview.pressure_value"'], ['])']],
        [['        '], ['k', 'return'], [' value_of(r)']],
    ];
}

const TOKEN = { k: styles.tokKey, s: styles.tokStr, f: styles.tokFn, d: styles.tokDoc };

export default function TrialGate({ t, lang }) {
    const g = t.gate;
    return (
        <>
            <Intro eyebrow={g.eyebrow} title={g.title} sub={g.sub} />

            <div className={styles.gateGrid}>
                <Reveal className={styles.trial} delay={0.1}>
                    <div className={styles.trialHead}>
                        <code>try_question</code>
                        <span>read_number · overview.pressure_value</span>
                    </div>
                    <table className={styles.trialTable}>
                        <thead>
                            <tr>
                                {g.columns.map((c) => <th key={c}>{c}</th>)}
                                <th aria-label="result" />
                            </tr>
                        </thead>
                        <tbody>
                            {STATES.map((s, i) => (
                                <motion.tr
                                    key={s}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.4, delay: 0.5 + i * 0.35, ease: EASE }}
                                >
                                    <td>
                                        <div className={styles.trialShot}>
                                            <HmiCrop state={s} box={pad(T.box, 4)} label={`overview_${s}.png`} />
                                        </div>
                                    </td>
                                    <td className={styles.mono}>{HMI_STATE[s].pressure}</td>
                                    <td className={styles.mono}>{HMI_STATE[s].pressure}</td>
                                    <td className={styles.trialOk}><CheckRoundedIcon sx={{ fontSize: 18 }} /></td>
                                </motion.tr>
                            ))}
                        </tbody>
                    </table>
                    <motion.p
                        className={styles.trialPass}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 1.7, ease: EASE }}
                    >
                        <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />
                        {g.passed}
                    </motion.p>
                </Reveal>

                <motion.div
                    className={styles.gateCode}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 2, ease: EASE }}
                >
                    <p className={styles.label}>{g.method} · <code>pages/overview.py</code></p>
                    <pre className={styles.libCode}>
                        {methodCode(lang).map((segs, i) => (
                            <div key={i}>
                                {segs.length === 0 ? ' ' : segs.map((seg, j) => (
                                    seg.length === 1
                                        ? <span key={j}>{seg[0]}</span>
                                        : <span key={j} className={TOKEN[seg[0]]}>{seg[1]}</span>
                                ))}
                            </div>
                        ))}
                    </pre>
                </motion.div>
            </div>

            <p className={styles.note}>{g.note}</p>
        </>
    );
}
